import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { fetchPortfolioPositions } from '../api/orders';
import { fetchDhanHoldings, fetchDhanOrders, fetchDhanPositions } from '../api/dhan';
import { useAuth } from '../auth/AuthContext';

const resolveUserId = (user) => user?.id || user?.user_id || user?.email || '';

const toInr = (value) => `₹${Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const nextMoveColor = {
  Hold: 'default',
  TrailSL: 'primary',
  BookPartial: 'secondary',
  ExitSL: 'error',
  ExitTarget: 'success',
};
const orderStatusColor = {
  PLACED: 'primary',
  FILLED: 'success',
  REJECTED: 'error',
  CANCELLED: 'default',
};

const num = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};
const isMissingResourceError = (err) => {
  const msg = String(err?.message || '').toLowerCase();
  return msg.includes('not found') || msg.includes('404');
};
const isLiveExecution = (row) => String(row?.execution_mode || 'live').toLowerCase() === 'live';
const normUpper = (v, fallback = '') => String(v || fallback).trim().toUpperCase();

const mapOrderRowToTimelineRow = (row, idx = 0) => {
  const symbol = normUpper(
    row?.symbol
    || row?.tradingSymbol
    || row?.securityId
    || row?.scripName
    || ''
  );
  const sideRaw = row?.side || row?.transactionType || row?.orderSide || row?.buySell;
  const statusRaw = row?.status || row?.orderStatus || row?.order_state || row?.state;
  const qty = num(row?.qty ?? row?.quantity ?? row?.orderQty ?? row?.filledQty ?? row?.tradedQty);
  const price = num(row?.price ?? row?.orderPrice ?? row?.averagePrice ?? row?.avgPrice ?? row?.tradedPrice);
  const updatedAt = row?.updated_at || row?.updateTime || row?.exchangeTime || row?.created_at || row?.createTime || row?.timestamp;
  const rejection = row?.rejection_reason || row?.rejectionReason || row?.omsErrorDescription || row?.rejectReason;
  const brokerOrderId = row?.broker_order_id || row?.brokerOrderId || row?.orderId;

  return {
    ...row,
    id: String(row?.id || brokerOrderId || `${symbol || 'ORDER'}-${idx}`),
    symbol,
    side: normUpper(sideRaw),
    status: normUpper(statusRaw || 'PLACED'),
    qty,
    price,
    execution_mode: String(row?.execution_mode || 'live').toLowerCase(),
    broker_order_id: brokerOrderId || null,
    rejection_reason: rejection || null,
    broker_response: row?.broker_response || row?.brokerResponse || row,
    updated_at: updatedAt || new Date().toISOString(),
  };
};

const mapDhanRowToPortfolioPosition = (row, source = 'position') => {
  const symbol = String(
    row?.tradingSymbol
    || row?.symbol
    || row?.securityId
    || row?.scripName
    || ''
  ).trim().toUpperCase();
  const qty = num(
    row?.netQty
    ?? row?.net_qty
    ?? row?.totalQty
    ?? row?.totalQuantity
    ?? row?.availableQty
    ?? row?.availableQuantity
    ?? row?.holdingQty
    ?? row?.holdingQuantity
    ?? (
      num(row?.dpQty ?? row?.dpQuantity)
      + num(row?.t1Qty ?? row?.t1Quantity)
      + num(row?.availableQty ?? row?.availableQuantity)
    )
    ?? row?.quantity
    ?? row?.qty
  );
  const avgPrice = num(row?.buyAvg ?? row?.averagePrice ?? row?.avgPrice ?? row?.avgCostPrice);
  const ltp = num(row?.ltp ?? row?.lastTradedPrice ?? row?.lastPrice ?? row?.close);
  const realized = source === 'holding'
    ? 0
    : num(row?.realizedProfit ?? row?.realizedPnl ?? row?.realized_pnl);
  const unrealizedRaw = num(row?.unrealizedProfit ?? row?.unrealizedPnl ?? row?.unrealized_pnl ?? row?.pnl);
  const unrealized = unrealizedRaw !== 0
    ? unrealizedRaw
    : ((qty > 0 && avgPrice > 0 && ltp > 0) ? ((ltp - avgPrice) * qty) : 0);
  const state = source === 'holding'
    ? (qty > 0 ? 'OPEN' : 'CLOSED')
    : (qty === 0 ? 'CLOSED' : String(row?.positionType || row?.state || 'OPEN').toUpperCase());

  return {
    id: String(row?.securityId || row?.positionId || row?.id || `${symbol}-${source}`),
    symbol,
    broker: 'dhan',
    product_type: String(row?.productType || row?.product || (source === 'holding' ? 'DELIVERY' : 'INTRADAY')).toUpperCase(),
    net_qty: qty,
    avg_price: avgPrice,
    avg_exit_price: num(row?.sellAvg),
    ltp,
    realized_pnl: realized,
    unrealized_pnl: unrealized,
    state,
    next_move: 'Hold',
    updated_at: row?.updatedAt || row?.updated_at || row?.timestamp || new Date().toISOString(),
  };
};

function PortfolioManagerPage() {
  const { user } = useAuth();
  const userId = resolveUserId(user);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);

  const load = useCallback(async () => {
    if (!userId) {
      setPositions([]);
      setOrders([]);
      setBusy(false);
      return;
    }
    setBusy(true);
    setError('');
    try {
      const [portfolioPosRes, dhanPosRes, dhanHoldRes, dhanOrderRes] = await Promise.allSettled([
        fetchPortfolioPositions({ userId }),
        fetchDhanPositions({ userId }),
        fetchDhanHoldings({ userId }),
        fetchDhanOrders({ userId }),
      ]);

      const resolvedPortfolio = portfolioPosRes.status === 'fulfilled'
        ? (Array.isArray(portfolioPosRes.value) ? portfolioPosRes.value : [])
        : [];
      const resolvedDhanPositions = dhanPosRes.status === 'fulfilled'
        ? (Array.isArray(dhanPosRes.value) ? dhanPosRes.value : [])
        : [];
      const resolvedDhanHoldings = dhanHoldRes.status === 'fulfilled'
        ? (Array.isArray(dhanHoldRes.value) ? dhanHoldRes.value : [])
        : [];
      let resolvedOrders = dhanOrderRes.status === 'fulfilled'
        ? (Array.isArray(dhanOrderRes.value) ? dhanOrderRes.value : [])
        : [];
      resolvedOrders = resolvedOrders
        .map((row, idx) => mapOrderRowToTimelineRow(row, idx))
        .filter(isLiveExecution);

      let resolvedPositions = Array.isArray(resolvedPortfolio) ? resolvedPortfolio : [];

      // Fallback to broker live data when local trade tables are empty.
      if (!resolvedPositions.length) {
        const mappedLiveRows = [
          ...resolvedDhanPositions.map((row) => mapDhanRowToPortfolioPosition(row, 'position')),
          ...resolvedDhanHoldings.map((row) => mapDhanRowToPortfolioPosition(row, 'holding')),
        ].filter((row) => row.symbol);
        const byKey = new Map();
        mappedLiveRows.forEach((row) => {
          const key = `${row.symbol}_${row.product_type}_${row.broker}`;
          if (!byKey.has(key)) byKey.set(key, row);
        });
        resolvedPositions = Array.from(byKey.values());
      }

      setPositions(resolvedPositions);
      setOrders(resolvedOrders);
      if (
        portfolioPosRes.status === 'rejected'
        && !isMissingResourceError(portfolioPosRes.reason)
        && !String(portfolioPosRes.reason?.message || '').toLowerCase().includes('dhan is not connected')
      ) {
        throw portfolioPosRes.reason;
      }
    } catch (e) {
      setError(e?.message || 'Failed to load portfolio manager data');
    } finally {
      setBusy(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const { openPositions, closedPositions } = useMemo(() => {
    const open = [];
    const closed = [];
    for (const row of positions) {
      const qty = num(row?.net_qty);
      const state = String(row?.state || '').toUpperCase();
      if (qty <= 0 || state === 'CLOSED') {
        closed.push({ ...row, state: 'CLOSED', net_qty: qty });
      } else {
        open.push({ ...row, state: 'OPEN', net_qty: qty });
      }
    }
    return { openPositions: open, closedPositions: closed };
  }, [positions]);

  const netSummary = useMemo(() => {
    return positions.reduce(
      (acc, row) => {
        acc.realized += Number(row?.realized_pnl || 0);
        acc.unrealized += Number(row?.unrealized_pnl || 0);
        return acc;
      },
      { realized: 0, unrealized: 0 }
    );
  }, [positions]);

  const timelineRows = useMemo(
    () =>
      [...orders]
        .sort((a, b) => new Date(b?.updated_at || b?.created_at || 0).getTime() - new Date(a?.updated_at || a?.created_at || 0).getTime())
        .slice(0, 50),
    [orders]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Portfolio Manager
        </Typography>
        <Button variant="outlined" onClick={load} disabled={busy} sx={{ textTransform: 'none' }}>
          Refresh
        </Button>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} sx={{ mb: 2 }}>
        <Chip label={`Open Positions: ${openPositions.length}`} color="primary" variant="outlined" />
        <Chip label={`Closed Positions: ${closedPositions.length}`} color="default" variant="outlined" />
        <Chip label={`Realized PnL: ${toInr(netSummary.realized)}`} color={netSummary.realized >= 0 ? 'success' : 'error'} />
        <Chip label={`Unrealized PnL: ${toInr(netSummary.unrealized)}`} color={netSummary.unrealized >= 0 ? 'success' : 'error'} />
        <Chip label={`Net PnL: ${toInr(netSummary.realized + netSummary.unrealized)}`} color={netSummary.realized + netSummary.unrealized >= 0 ? 'success' : 'error'} />
      </Stack>

      {busy ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.2 }}>Open Positions</Typography>
            {!openPositions.length ? (
              <Typography sx={{ color: '#666', fontSize: 13 }}>No open positions.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Broker</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Avg Price</TableCell>
                      <TableCell align="right">LTP</TableCell>
                      <TableCell align="right">Unrealized PnL</TableCell>
                      <TableCell>Next Move</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {openPositions.map((row) => (
                      <TableRow key={`${row.id}-${row.symbol}`}>
                        <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                        <TableCell>{String(row.broker || '').toUpperCase()}</TableCell>
                        <TableCell>{row.product_type}</TableCell>
                        <TableCell align="right">{row.net_qty}</TableCell>
                        <TableCell align="right">{toInr(row.avg_price)}</TableCell>
                        <TableCell align="right">{toInr(row.ltp)}</TableCell>
                        <TableCell align="right" sx={{ color: Number(row.unrealized_pnl || 0) >= 0 ? '#1b5e20' : '#c62828', fontWeight: 700 }}>
                          {toInr(row.unrealized_pnl)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.next_move || 'Hold'}
                            color={nextMoveColor[row.next_move] || 'default'}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.2 }}>Closed Trades</Typography>
            {!closedPositions.length ? (
              <Typography sx={{ color: '#666', fontSize: 13 }}>No closed trades yet.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Broker</TableCell>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Realized PnL</TableCell>
                      <TableCell>State</TableCell>
                      <TableCell>Next Move</TableCell>
                      <TableCell>Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {closedPositions.map((row) => (
                      <TableRow key={`${row.id}-${row.symbol}`}>
                        <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                        <TableCell>{String(row.broker || '').toUpperCase()}</TableCell>
                        <TableCell>{row.product_type}</TableCell>
                        <TableCell align="right" sx={{ color: Number(row.realized_pnl || 0) >= 0 ? '#1b5e20' : '#c62828', fontWeight: 700 }}>
                          {toInr(row.realized_pnl)}
                        </TableCell>
                        <TableCell>{row.state}</TableCell>
                        <TableCell>
                          <Chip size="small" label={row.next_move || 'ExitTarget'} color={nextMoveColor[row.next_move] || 'default'} />
                        </TableCell>
                        <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleString() : '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.2 }}>Order Timeline + Charge Breakup</Typography>
            {!timelineRows.length ? (
              <Typography sx={{ color: '#666', fontSize: 13 }}>No orders available.</Typography>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Time</TableCell>
                      <TableCell>Symbol</TableCell>
                      <TableCell>Side</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Execution</TableCell>
                      <TableCell>Broker Order ID</TableCell>
                      <TableCell>Broker Response</TableCell>
                      <TableCell align="right">Qty</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="right">Charges</TableCell>
                      <TableCell align="right">STT</TableCell>
                      <TableCell align="right">GST</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timelineRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>{row.updated_at ? new Date(row.updated_at).toLocaleString() : new Date(row.created_at).toLocaleString()}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.side}
                            color={row.side === 'BUY' ? 'success' : 'error'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={row.status || '—'}
                            color={orderStatusColor[String(row.status || '').toUpperCase()] || 'default'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{String(row.execution_mode || 'live').toUpperCase()}</TableCell>
                        <TableCell>{row.broker_order_id || '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          {row.rejection_reason ? (
                            <Tooltip title={String(row.rejection_reason)}>
                              <span>{String(row.rejection_reason).slice(0, 80)}{String(row.rejection_reason).length > 80 ? '…' : ''}</span>
                            </Tooltip>
                          ) : (
                            row.broker_response
                              ? (
                                <Tooltip title={typeof row.broker_response === 'string' ? row.broker_response : JSON.stringify(row.broker_response)}>
                                  <span>Broker ack received</span>
                                </Tooltip>
                              )
                              : '—'
                          )}
                        </TableCell>
                        <TableCell align="right">{row.qty}</TableCell>
                        <TableCell align="right">{toInr(row.price)}</TableCell>
                        <TableCell align="right">{toInr(row?.charge_breakup?.total_charges)}</TableCell>
                        <TableCell align="right">{toInr(row?.charge_breakup?.stt)}</TableCell>
                        <TableCell align="right">{toInr(row?.charge_breakup?.gst)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}

export default PortfolioManagerPage;
